import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Loader2, CheckCircle, Home, Eye, EyeOff, Phone, Mail, User, Lock, RefreshCw } from 'lucide-react';

// ─── Validation helpers ────────────────────────────────────────────────────────
const validatePhone = (phone) => /^[6-9]\d{9}$/.test(phone.replace(/\s/g, ''));
const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
const validatePassword = (pwd) => pwd.length >= 8;

export default function AdminRegisterPage() {
  const [step, setStep] = useState(1); // 1: Register form, 2: OTP verification
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  // Registration form state
  const [formData, setFormData] = useState({
    name: '', email: '', mobile: '', password: '', confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errors, setErrors] = useState({});

  // OTP state
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [otpFromServer, setOtpFromServer] = useState(''); // dev only
  const otpRefs = useRef([]);

  const { registerAdmin, verifyOTP } = useAuth();
  const navigate = useNavigate();

  // ─── Resend timer countdown ────────────────────────────────────────────────
  useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setTimeout(() => setResendTimer(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendTimer]);

  // ─── Field-level validation ────────────────────────────────────────────────
  const validate = () => {
    const errs = {};
    if (!formData.name.trim()) errs.name = 'Full name is required';
    else if (formData.name.trim().length < 2) errs.name = 'Name must be at least 2 characters';

    if (!formData.email.trim()) errs.email = 'Email address is required';
    else if (!validateEmail(formData.email)) errs.email = 'Enter a valid email address';

    if (!formData.mobile.trim()) errs.mobile = 'Phone number is required';
    else if (!validatePhone(formData.mobile)) errs.mobile = 'Enter a valid 10-digit Indian mobile number (starts with 6-9)';

    if (!formData.password) errs.password = 'Password is required';
    else if (!validatePassword(formData.password)) errs.password = 'Password must be at least 8 characters';

    if (!formData.confirmPassword) errs.confirmPassword = 'Please confirm your password';
    else if (formData.password !== formData.confirmPassword) errs.confirmPassword = 'Passwords do not match';

    return errs;
  };

  const handleFieldChange = (field, value) => {
    setFormData(f => ({ ...f, [field]: value }));
    // Clear error as user types
    if (errors[field]) setErrors(e => ({ ...e, [field]: '' }));
  };

  // ─── Step 1: Register ──────────────────────────────────────────────────────
  const handleRegister = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      // Show first error as toast
      toast.error(Object.values(errs)[0]);
      return;
    }
    setLoading(true);
    try {
      const data = await registerAdmin(formData);
      setEmail(formData.email);
      if (data.otp) setOtpFromServer(data.otp); // dev mode only
      setResendTimer(60);
      setStep(2);
      toast.success('OTP sent! Check your email.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  // ─── OTP input handling ────────────────────────────────────────────────────
  const handleOtpChange = (index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const newOtp = [...otp];
    newOtp[index] = digit;
    setOtp(newOtp);
    // Auto-advance
    if (digit && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && index > 0) otpRefs.current[index - 1]?.focus();
    if (e.key === 'ArrowRight' && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length > 0) {
      const newOtp = ['', '', '', '', '', ''];
      pasted.split('').forEach((ch, i) => { newOtp[i] = ch; });
      setOtp(newOtp);
      const nextEmpty = pasted.length < 6 ? pasted.length : 5;
      otpRefs.current[nextEmpty]?.focus();
    }
  };

  const otpValue = otp.join('');

  // ─── Step 2: Verify OTP ────────────────────────────────────────────────────
  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (otpValue.length !== 6) return toast.error('Please enter all 6 digits of the OTP');
    setLoading(true);
    try {
      await verifyOTP(email, otpValue);
      toast.success('Account verified! Welcome aboard! 🎉');
      navigate('/admin/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.message || 'OTP verification failed');
      // Clear OTP on failure
      setOtp(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  // ─── Resend OTP ────────────────────────────────────────────────────────────
  const handleResendOTP = async () => {
    if (resendTimer > 0 || resendLoading) return;
    setResendLoading(true);
    try {
      const { data } = await api.post('/auth/admin/resend-otp', { email });
      if (data.otp) setOtpFromServer(data.otp);
      setOtp(['', '', '', '', '', '']);
      setResendTimer(60);
      toast.success('OTP resent! Check your email.');
      otpRefs.current[0]?.focus();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to resend OTP');
    } finally {
      setResendLoading(false);
    }
  };

  // ─── UI helpers ───────────────────────────────────────────────────────────
  const FieldError = ({ msg }) =>
    msg ? <p className="text-xs text-red-500 mt-1">{msg}</p> : null;

  const inputCls = (field) =>
    `input-field pr-10 ${errors[field] ? 'border-red-400 focus:ring-red-300 focus:border-red-400' : ''}`;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      {/* Background accents */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-yellow-300 opacity-20 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-yellow-200 opacity-15 blur-3xl" />
      </div>

      <div className="w-full max-w-md relative z-10">

        {/* Back link */}
        <div className="mb-5">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-black transition-colors font-medium">
            <Home size={15} /> Back to Home
          </Link>
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-2xl shadow-md mb-4 p-2 border border-gray-100">
            <img src="/bits-logo.png" alt="BITS Logo" className="w-full h-full object-contain" />
          </div>
          <h1 className="text-2xl font-bold text-black">Admin Registration</h1>
          <p className="text-gray-500 text-sm mt-1">Create your administrator account</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">

          {/* Step indicator */}
          <div className="flex items-center mb-8">
            <div className={`flex items-center justify-center w-9 h-9 rounded-full text-sm font-bold border-2 transition-all ${
              step >= 1 ? 'bg-yellow-400 border-yellow-400 text-black' : 'bg-white border-gray-300 text-gray-400'
            }`}>
              {step > 1 ? <CheckCircle size={18} /> : '1'}
            </div>
            <div className="flex-1 mx-3">
              <div className={`h-1 rounded-full transition-all duration-500 ${step > 1 ? 'bg-yellow-400' : 'bg-gray-200'}`} />
            </div>
            <div className={`flex items-center justify-center w-9 h-9 rounded-full text-sm font-bold border-2 transition-all ${
              step >= 2 ? 'bg-yellow-400 border-yellow-400 text-black' : 'bg-white border-gray-300 text-gray-400'
            }`}>
              2
            </div>
          </div>

          {/* ── STEP 1: Registration Form ── */}
          {step === 1 ? (
            <form onSubmit={handleRegister} className="space-y-4" noValidate>
              <h2 className="text-lg font-bold text-gray-800 mb-1">Create Account</h2>
              <p className="text-xs text-gray-400 mb-4">All fields marked * are required</p>

              {/* Full Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <User size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    id="reg-name"
                    type="text"
                    className={`input-field pl-9 ${errors.name ? 'border-red-400 focus:ring-red-300 focus:border-red-400' : ''}`}
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={e => handleFieldChange('name', e.target.value)}
                    autoComplete="name"
                  />
                </div>
                <FieldError msg={errors.name} />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    id="reg-email"
                    type="email"
                    className={`input-field pl-9 ${errors.email ? 'border-red-400 focus:ring-red-300 focus:border-red-400' : ''}`}
                    placeholder="admin@example.com"
                    value={formData.email}
                    onChange={e => handleFieldChange('email', e.target.value)}
                    autoComplete="email"
                  />
                </div>
                <FieldError msg={errors.email} />
              </div>

              {/* Phone — MANDATORY */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Mobile Number <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
                    <Phone size={15} className="text-gray-400" />
                    <span className="text-xs text-gray-400 font-medium border-r border-gray-300 pr-2">+91</span>
                  </div>
                  <input
                    id="reg-mobile"
                    type="tel"
                    maxLength={10}
                    className={`input-field pl-16 ${errors.mobile ? 'border-red-400 focus:ring-red-300 focus:border-red-400' : ''}`}
                    placeholder="9876543210"
                    value={formData.mobile}
                    onChange={e => handleFieldChange('mobile', e.target.value.replace(/\D/g, '').slice(0, 10))}
                    autoComplete="tel"
                  />
                </div>
                <FieldError msg={errors.mobile} />
                {!errors.mobile && (
                  <p className="text-xs text-gray-400 mt-1">10-digit number starting with 6, 7, 8, or 9</p>
                )}
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    id="reg-password"
                    type={showPassword ? 'text' : 'password'}
                    className={`input-field pl-9 pr-10 ${errors.password ? 'border-red-400 focus:ring-red-300 focus:border-red-400' : ''}`}
                    placeholder="Min. 8 characters"
                    value={formData.password}
                    onChange={e => handleFieldChange('password', e.target.value)}
                    autoComplete="new-password"
                  />
                  <button type="button" onClick={() => setShowPassword(s => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <FieldError msg={errors.password} />
                {/* Password strength indicator */}
                {formData.password && (
                  <div className="mt-2">
                    <div className="flex gap-1">
                      {[
                        formData.password.length >= 8,
                        /[A-Z]/.test(formData.password),
                        /[0-9]/.test(formData.password),
                        /[^A-Za-z0-9]/.test(formData.password)
                      ].map((met, i) => (
                        <div key={i} className={`h-1 flex-1 rounded-full transition-all ${met ? 'bg-yellow-400' : 'bg-gray-200'}`} />
                      ))}
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      Tip: Add uppercase, numbers &amp; symbols for a stronger password
                    </p>
                  </div>
                )}
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Confirm Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    id="reg-confirm-password"
                    type={showConfirm ? 'text' : 'password'}
                    className={`input-field pl-9 pr-10 ${errors.confirmPassword ? 'border-red-400 focus:ring-red-300 focus:border-red-400' : ''}`}
                    placeholder="Re-enter password"
                    value={formData.confirmPassword}
                    onChange={e => handleFieldChange('confirmPassword', e.target.value)}
                    autoComplete="new-password"
                  />
                  <button type="button" onClick={() => setShowConfirm(s => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                <FieldError msg={errors.confirmPassword} />
                {/* Match indicator */}
                {formData.confirmPassword && formData.password && !errors.confirmPassword && (
                  <p className={`text-xs mt-1 ${formData.password === formData.confirmPassword ? 'text-green-600' : 'text-red-500'}`}>
                    {formData.password === formData.confirmPassword ? '✓ Passwords match' : '✗ Passwords do not match'}
                  </p>
                )}
              </div>

              <button
                type="submit"
                id="register-btn"
                disabled={loading}
                className="btn-primary w-full py-3 justify-center text-sm font-semibold gap-2 mt-2"
              >
                {loading && <Loader2 className="animate-spin" size={17} />}
                {loading ? 'Registering...' : 'Register & Send OTP'}
              </button>
            </form>

          ) : (
            /* ── STEP 2: OTP Verification ── */
            <form onSubmit={handleVerifyOTP} className="space-y-5">
              <div>
                <h2 className="text-lg font-bold text-gray-800">Verify Your Email</h2>
                <p className="text-sm text-gray-500 mt-1">
                  We sent a 6-digit OTP to{' '}
                  <strong className="text-black">{email}</strong>
                </p>
              </div>

              {/* Dev OTP display */}
              {otpFromServer && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-start gap-2">
                  <span className="text-amber-500 text-lg">🔑</span>
                  <div>
                    <p className="text-xs font-bold text-amber-700 mb-0.5">Development Mode OTP</p>
                    <p className="text-lg font-mono font-bold text-amber-900 tracking-widest">{otpFromServer}</p>
                  </div>
                </div>
              )}

              {/* OTP boxes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Enter 6-digit OTP</label>
                <div className="flex gap-2 justify-center" onPaste={handleOtpPaste}>
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      ref={el => otpRefs.current[index] = el}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={e => handleOtpChange(index, e.target.value)}
                      onKeyDown={e => handleOtpKeyDown(index, e)}
                      className={`w-11 h-12 text-center text-xl font-bold font-mono border-2 rounded-xl outline-none transition-all
                        ${digit ? 'border-yellow-400 bg-yellow-50 text-black' : 'border-gray-300 bg-white text-gray-400'}
                        focus:border-yellow-500 focus:ring-2 focus:ring-yellow-200`}
                      autoComplete="off"
                    />
                  ))}
                </div>
              </div>

              {/* Verify button */}
              <button
                type="submit"
                id="verify-otp-btn"
                disabled={loading || otpValue.length !== 6}
                className="btn-primary w-full py-3 justify-center text-sm font-semibold gap-2"
              >
                {loading && <Loader2 className="animate-spin" size={17} />}
                {loading ? 'Verifying...' : 'Verify & Activate Account'}
              </button>

              {/* Resend OTP */}
              <div className="text-center">
                <p className="text-sm text-gray-500">
                  Didn&apos;t receive the OTP?{' '}
                  {resendTimer > 0 ? (
                    <span className="text-gray-400 font-medium">
                      Resend in <strong className="text-black">{resendTimer}s</strong>
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={handleResendOTP}
                      disabled={resendLoading}
                      className="inline-flex items-center gap-1 text-black font-semibold hover:underline disabled:opacity-50"
                    >
                      {resendLoading
                        ? <><Loader2 size={13} className="animate-spin" /> Sending...</>
                        : <><RefreshCw size={13} /> Resend OTP</>
                      }
                    </button>
                  )}
                </p>
              </div>

              {/* Back link */}
              <div className="text-center pt-1">
                <button
                  type="button"
                  onClick={() => { setStep(1); setOtp(['', '', '', '', '', '']); }}
                  className="text-sm text-gray-500 hover:text-black transition-colors"
                >
                  ← Wrong email? Go back
                </button>
              </div>
            </form>
          )}

          <p className="text-center text-sm text-gray-500 mt-6">
            Already have an account?{' '}
            <Link to="/admin/login" className="text-black font-semibold hover:underline">
              Login here
            </Link>
          </p>
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">
          © {new Date().getFullYear()} BITS Vizag EAMCET Portal. All rights reserved.
        </p>
      </div>
    </div>
  );
}
